import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { firestore } from "@/plugins/firebase.app";

/**
 * Normalizes string for case-insensitive and accent-insensitive matching.
 */
export const normalizeForMatch = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

/**
 * Finnish to EuroNCAP English body type mappings.
 */
export const BODY_TYPE_TRANSLATIONS = {
  farmari: "stationwagon",
  sedan: "sedan",
  viistoperä: "hatchback",
  viistopera: "hatchback",
  hatchback: "hatchback",
  maastoauto: "suv",
  suv: "suv",
  coupe: "coupé",
  coupé: "coupé",
  avoauto: "cabrio",
  cabrio: "cabrio",
  tilaauto: "mpv",
  mpv: "mpv",
  pakettiauto: "van",
  van: "van",
};

/**
 * Selects the best rescue sheet URL from documents (prefers FI, then EN, then first).
 */
export const pickRescueSheetUrl = (documents) => {
  const sheets = (documents || []).filter((d) => d.type === "Rescue Sheet");
  if (sheets.length === 0) return null;
  return (
    sheets.find((d) => d.language === "FI") ||
    sheets.find((d) => d.language === "EN") ||
    sheets[0]
  ).url;
};

/**
 * Scores a variant candidate against vehicle attributes.
 */
export const scoreVariant = (
  v,
  { normalizedModel, year, normalizedBodyType },
) => {
  let score = 0;
  const vModel = v.modelNormalized || "";
  if (normalizedModel) {
    if (vModel === normalizedModel) {
      score += 50;
    } else if (
      vModel.includes(normalizedModel) ||
      normalizedModel.includes(vModel)
    ) {
      score += 20;
    }
  }
  if (year) {
    const from = parseInt(v.build_year_from, 10) || 0;
    const until = v.build_year_until
      ? parseInt(v.build_year_until, 10)
      : new Date().getFullYear() + 1;
    if (year >= from && year <= until) {
      score += 100;
    } else {
      score -= Math.min(Math.abs(year - from), Math.abs(year - until));
    }
  }
  if (
    normalizedBodyType &&
    normalizeForMatch(v.body_type) === normalizedBodyType
  ) {
    score += 15;
  }
  return score;
};

/**
 * Normalizes a raw Firestore variant document into the format expected by UI components.
 */
export const formatVariant = (v, score = null) => ({
  id: v.id,
  name: v.name,
  make_name: v.make_name,
  modelName: v.model_name || v.name,
  bodyType: v.body_type,
  buildYearFrom: v.build_year_from,
  buildYearUntil: v.build_year_until,
  doors: v.doors,
  powertrain: v.powertrain,
  pictureUrl: v.picture_url,
  rescueSheetUrl: pickRescueSheetUrl(v.documents),
  documents: v.documents || [],
  ...(score !== null ? { score } : {}),
});

/**
 * Fetches EuroNCAP metadata document directly from Firestore.
 */
export const fetchRescueMetadata = async () => {
  const metaDocRef = doc(firestore, "pelastuskortit", "metadata");
  const snap = await getDoc(metaDocRef);
  if (!snap.exists()) {
    return {
      exists: false,
      makes: [],
      count: 0,
      updatedAt: null,
      makeLogos: {},
    };
  }
  const data = snap.data();
  return {
    exists: true,
    makes: data.makes || [],
    count: data.count || 0,
    updatedAt: data.updatedAt || null,
    makeLogos: data.makeLogos || {},
  };
};

/**
 * Fetches and groups models for a given make directly from Firestore.
 */
export const fetchRescueModelsByMake = async (make) => {
  const normalizedMake = normalizeForMatch(make);
  if (!normalizedMake) return { models: [], rawVariants: [] };

  const variantsColRef = collection(
    firestore,
    "pelastuskortit",
    "metadata",
    "variants",
  );
  const q = query(
    variantsColRef,
    where("makeNormalized", "==", normalizedMake),
  );
  const snap = await getDocs(q);

  const models = new Map();
  const rawVariants = [];

  snap.docs.forEach((d) => {
    const v = d.data();
    rawVariants.push(v);
    const key = v.modelNormalized;
    if (!key) return;
    const existing = models.get(key);
    if (existing) {
      existing.count += 1;
      existing.yearFrom = Math.min(
        existing.yearFrom,
        parseInt(v.build_year_from, 10) || existing.yearFrom,
      );
      existing.yearUntil = Math.max(
        existing.yearUntil,
        parseInt(v.build_year_until, 10) || existing.yearUntil || 0,
      );
      if (
        v.picture_url &&
        existing.pictureUrls.length < 4 &&
        !existing.pictureUrls.includes(v.picture_url)
      ) {
        existing.pictureUrls.push(v.picture_url);
      }
    } else {
      models.set(key, {
        model: v.model_name || v.name,
        count: 1,
        yearFrom: parseInt(v.build_year_from, 10) || null,
        yearUntil: parseInt(v.build_year_until, 10) || null,
        pictureUrls: v.picture_url ? [v.picture_url] : [],
      });
    }
  });

  return {
    models: [...models.values()].sort((a, b) =>
      (a.model || "").localeCompare(b.model || ""),
    ),
    rawVariants,
  };
};

/**
 * Fetches variants for a specific make and model directly from Firestore.
 */
export const fetchRescueVariantsByModel = async (make, model) => {
  const normalizedMake = normalizeForMatch(make);
  const normalizedModel = normalizeForMatch(model);
  if (!normalizedMake || !normalizedModel) return { variants: [] };

  const variantsColRef = collection(
    firestore,
    "pelastuskortit",
    "metadata",
    "variants",
  );
  const q = query(
    variantsColRef,
    where("makeNormalized", "==", normalizedMake),
  );
  const snap = await getDocs(q);

  const variants = snap.docs
    .map((d) => d.data())
    .filter((v) => v.modelNormalized === normalizedModel)
    .sort(
      (a, b) =>
        (parseInt(a.build_year_from, 10) || 0) -
        (parseInt(b.build_year_from, 10) || 0),
    )
    .map((v) => formatVariant(v));

  return { variants };
};

/**
 * Searches and ranks rescue sheets for a vehicle directly via Firestore query and client-side scoring.
 */
export const searchRescueSheets = async ({
  make,
  model,
  year,
  bodyType,
}) => {
  const normalizedMake = normalizeForMatch(make);
  const normalizedModel = normalizeForMatch(model);
  const parsedYear = parseInt(year, 10) || null;
  const translatedBodyType =
    BODY_TYPE_TRANSLATIONS[normalizeForMatch(bodyType)] || bodyType;
  const normalizedBodyType = normalizeForMatch(translatedBodyType);

  if (!normalizedMake) return { suggestedId: null, variants: [] };

  const variantsColRef = collection(
    firestore,
    "pelastuskortit",
    "metadata",
    "variants",
  );
  const q = query(
    variantsColRef,
    where("makeNormalized", "==", normalizedMake),
  );
  const snap = await getDocs(q);

  let candidates = snap.docs.map((d) => d.data());
  if (candidates.length === 0) {
    return { suggestedId: null, variants: [] };
  }

  if (normalizedModel) {
    const modelFiltered = candidates.filter((v) => {
      const vModel = v.modelNormalized || "";
      return (
        vModel === normalizedModel ||
        vModel.includes(normalizedModel) ||
        normalizedModel.includes(vModel)
      );
    });
    if (modelFiltered.length > 0) {
      candidates = modelFiltered;
    }
  }

  const scored = candidates.map((v) => {
    const score = scoreVariant(v, {
      normalizedModel,
      year: parsedYear,
      normalizedBodyType,
    });
    return { variant: v, score };
  });

  scored.sort((a, b) => b.score - a.score);

  const result = scored
    .slice(0, 12)
    .map(({ variant: v, score }) => formatVariant(v, score));

  return {
    suggestedId: result.length > 0 ? result[0].id : null,
    variants: result,
  };
};
